import { A, Container, H1, H2, Ol, P, Strong } from '#app/ui/components'

export default function Privacy() {
	return (
		<Container className="py-16">
			<div className="mx-auto max-w-3xl space-y-8">
				<H1>Privacy Policy</H1>

				<P>
					Protecting Users' privacy means upholding our commitment to
					transparency in the use of Users' personal data.
				</P>

				<P>
					This privacy policy (hereinafter, "Privacy Policy") informs you about
					the data processing carried out by the web platform{` `}
					<A
						href="https://welovenight.com/"
						target="_blank"
						rel="noopener noreferrer"
					>
						https://welovenight.com/
					</A>{` `}
					(hereinafter, the "Platform") for users (the "Users") who visit the
					Platform. WELOVENIGHT values Users' trust and privacy, and for this
					reason explains below how users' data is processed. If Users have any
					questions or want to receive more information in this regard, they can
					write to WELOVENIGHT to:{` `}
					<A href="mailto:bielmartinez@welovenight.com">
						bielmartinez@welovenight.com
					</A>
					.
				</P>

				<Ol>
					<li className="space-y-4">
						<H2>Who Is The Data Controller?</H2>
					<P>
						The data controller is the person who decides the purpose for which
						Users' personal data is used, in this case, WE LOVE NIGHT S.L. ("WELOVENIGHT" or the "Company") with Tax ID B75524363 and
						registered office at Calle Miquel Marqués, 25. 07005, Palma de
						Mallorca (Illes Balears), Spain.
					</P>
						<P>
							You can contact the privacy officer of WELOVENIGHT at the following
							email:{` `}
							<A href="mailto:bielmartinez@welovenight.com">
								bielmartinez@welovenight.com
							</A>
							.
						</P>
					</li>

					<li className="space-y-4">
						<H2>What Data Is Processed In WELOVENIGHT?</H2>
					<P>
						Personal data ("Data") is any information about an identified or
						identifiable natural person (the "Data Subject"). The personal data
						that WELOVENIGHT processes comes from the information that Users
						provide when they fill in the forms found on the Platform, when
						browsing the Platform, when registering for programs and / or buying
						through the Platform and / or when sending queries or requests for
						information. Additionally, WELOVENIGHT processes data related to
						event attendance including purchase and validation times of tickets,
						event attendance history, access statistics (where users go, where
						they don't go, with whom they go), and for public relations
						personnel: email, phone, age, gender, and name.
					</P>

					<P>
						In each form WELOVENIGHT indicates the contact information that
						WELOVENIGHT need to be able to deal with Users' queries or requests
						by means of an asterisk. Data that is not marked as mandatory
						through an asterisk will be completely optional. Only the data
						necessary for each purpose is collected. Please note that if Users
						do not provide the mandatory information, WELOVENIGHT will not be
						able to attend to their query or request or manage the data
						processing. This same indication with an asterisk will also be
						applied to necessary data in the shopping cart.
					</P>

					<P>
						Please note that it is Users' responsibility to provide accurate
						data and keep them updated, so WELOVENIGHT reserves the right to
						exclude from the services and / or cancel them, Users who provide
						false, incorrect data or without the due authorization of the
						Interested Party, without prejudice to WELOVENIGHT's right to
						exercise as many legal actions as appropriate.
					</P>
					</li>

					<li className="space-y-4">
						<H2>For What Purpose Does WELOVENIGHT Process Users' Data?</H2>
					<P>
						WELOVENIGHT will process Users' Data for the following purposes:
					</P>

					<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
						<li>
							<P><Strong>Purchase Of Products</Strong></P>
							<P>
								When Users purchase a product or service ("Consumers"),
								WELOVENIGHT may use their Data to manage the purchase and send
								them the product, as well as to comply with the legal
								obligations that apply. WELOVENIGHT will also use their Data to
								contact them in relation to the purchase and to offer them
								offers and additional information about WELOVENIGHT related to
								it. The data provided for the purchase (email) may also be used to associate the purchaser with an existing Platform Account or with an account that may be created in the future.
							</P>
						</li>

						<li>
							<P><Strong>User Account And Registration</Strong></P>
							<P>
								When Users register on the Platform and create an account (the
								"Account"), WELOVENIGHT may use their information to: (i)
								process their registration and manage their Account; (ii) verify
								their identity; (iii) provide access to Platform functionalities
								according to their User role (iv) send them service-related
								notifications, updates and important information; (v) generate
								anonymised statistics to improve the service; (vi) prevent
								fraud, abuse and unauthorised access; and (vii) comply with any
								legal obligations applicable to WELOVENIGHT.
							</P>
						</li>

						<li>
							<P><Strong>Subscription To The Newsletter</Strong></P>
							<P>
								In the event that Users want to receive commercial
								communications or newsletters about WELOVENIGHT, its programs
								and events or those of third parties in which WELOVENIGHT
								participate through the email provided in the form (only if
								WELOVENIGHT has collaborators), WELOVENIGHT will use their
								contact information to manage the sending of these
								communications.
							</P>
						</li>

						<li>
							<P><Strong>Contact</Strong></P>
							<P>
								If Users contact WELOVENIGHT through any of the channels enabled
								in this regard, WELOVENIGHT will use the contact details and
								details they provide in the form to adequately manage their
								queries or requests
							</P>
						</li>

						<li>
							<P><Strong>Browsing Our Platform</Strong></P>
							<P>
								By cookies and other technologies, WELOVENIGHT can perform
								measurements and analyze browsing traffic, usability and Users'
								experience on the Platform statistically. The installation and
								use of cookies depend on the configuration Users make both in
								the cookie banner and through their browser.
							</P>
						</li>
					</Ol>
					</li>

					<li className="space-y-4">
						<H2>What Are The Legal Basis For The Processing Of Users' Data?</H2>
					<P>
						The legal basis for the processing of Users' Data for the purchase
						of products of WELOVENIGHT is the execution of the contract of sale
						and compliance with legal obligations. For associating the purchase ticket with the user registered on the Platform, the legal basis is the execution of the contract entered into when registering on the Platform.
					</P>

					<P>
						For User registration on the Platform, the legal basis for
						processing the User's Data is the consent provided for the creation of the Account and, when applicable, the execution of the purchase contract.
					</P>

					<P>
						The legal basis for sending commercial communications when Users
						subscribe to the newsletter and for contacting WELOVENIGHT is the
						consent they have given by registering on the online forms. In some
						cases, the processing of Users' Data when they contact WELOVENIGHT
						might be legitimated on the execution of pre-contractual measures if
						these are necessary to respond to the query.
					</P>

					<P>
						Please note that, if Users are buyers of WELOVENIGHT products and/or
						services, WELOVENIGHT may also send them communications based on its
						legitimate interest in offering them improvements, offers and
						activities that may interest them and keep them informed about the
						different activities and initiatives of WELOVENIGHT that it believes
						may be of interest to them, and compliance with its legal
						obligations (such as, for example, the tax return of contributions
						to WELOVENIGHT).
					</P>

					<P>
						The legal basis for the processing of Users' Data with regard to
						browsing the Platform is the consent that they give when they accept
						the installation of cookies and that they can revoke or configure at
						any time through the configuration panel. Users can find more
						information about the use of cookies in the Cookie Policy of
						WELOVENIGHT (once available).
					</P>

					<P>
						Please, remember that Users can revoke their consent at any time.
						They can also object to continuing receiving commercial
						communications in the manner indicated in the footer of each
						communication.
					</P>
					</li>

					<li className="space-y-4">
						<H2>How Long Is Users' Data Kept?</H2>
					<P>
						Users' personal data is conserved for the period necessary for the
						purpose for which they were collected, and specifically:
					</P>

					<Ol className="ml-4 space-y-2 [&>li::marker]:content-[counter(list-item,lower-alpha)')'] [&>li]:pl-2">
						<li>
							<P>
								In relation to the purchase of products of WELOVENIGHT,
								WELOVENIGHT will keep the User's Data for the time necessary to
								provide the User with the product and then keep them blocked
								during the conservation periods or limitation periods provided by
								law.
							</P>
						</li>
						<li>
							<P>
								In relation to the creation of a user account, WELOVENIGHT will
								keep Users' Data for as long as necessary until they delete their
								profile from the Platform and thereafter keep them blocked during
								the conservation periods provided by law.
							</P>
						</li>
						<li>
							<P>
								In relation to processing queries and managing requests, the data
								will be retained for as long as necessary to deal with the query
								and/or manage the request.
							</P>
						</li>
						<li>
							<P>
								In relation to communications, the data will be processed until
								Users revoke their consent, oppose the processing and / or request
								the deletion of their personal data, up to a maximum of two years.
							</P>
						</li>
						<li>
							<P>
								In relation to the Platform Browsing Data, until Users revoke
								their consent and at the latest within 1 year. In any case,
								information about web traffic is deleted periodically, so that
								WELOVENIGHT will only keep anonymized statistical data of use
								(without identification of Users).
							</P>
						</li>
					</Ol>

					<P>
						Once the data are no longer necessary for the purpose for which they
						are processed, they will remain blocked for the sole purpose of
						meeting any responsibility or obligations that may arise from the
						data processing thereof.
					</P>
					</li>

					<li className="space-y-4">
						<H2>To Which Recipients Will Users' Data Be Communicated?</H2>
					<P>
						Users' Data may be shared with the following categories of
						recipients: (i) Event Organisers: when Users purchase tickets for
						their events (name, email, telephone, attendance data); (ii) Payment
						processors: Stripe (payment data is processed directly by Stripe and
						not stored by WELOVENIGHT); (iii) Service providers: Hosting,
						technical support services, financial services, etc.); (iv) Event
						email, analytics and other technical service providers under data
						processing agreements (IT tools, promoters who manage events and
						venues that provide event spaces as necessary for event management.
						Personal data will not be sold or rented to third parties for
						marketing purposes.
					</P>

					<P>
						Some of the above suppliers may have processing and/or support
						centers located outside the European Economic Area (EEA).
						WELOVENIGHT guarantees that, when Users' Data could leave the EEA,
						it will maintain the same level of protection based on compliance
						with the provisions of European Data protection regulations. In this
						sense, international data transfers will be made (i) to countries
						with an adequate level of protection declared by the European
						Commission (ii) based on the provision of adequate guarantees
						(standard contractual clauses or binding corporate rules), (iii) by
						virtue of the authorization of the competent control authority or
						other cases provided for in the regulations.
					</P>

					<P>
						To receive more information about international data transfers or to
						obtain confirmation or a copy of the guarantees used, Users can
						contact WELOVENIGHT at{` `}
						<A href="mailto:bielmartinez@welovenight.com">
							bielmartinez@welovenight.com
						</A>
						.
					</P>

					<P>
						In cases where it is necessary to comply with the legal obligations
						of WELOVENIGHT, the data may be communicated to the Public
						Administration and the State Security Forces and Bodies.
					</P>
					</li>

					<li className="space-y-4">
						<H2>
							Registration And Monitoring Of Corporate Platform Social Network
							Accounts Platform
						</H2>
					<P>
						In the case of the profiles that WELOVENIGHT maintain in the
						different social networks linked from this Platform, for example,
						through plugins or third-party applications, Users' Data may be
						processed under other privacy policies of the owners of said social
						networks.
					</P>

					<P>
						The use of such plugins is voluntary. If Users choose to use them,
						they will be authorizing the communication of their personal data
						(IP address, browsing data) to the aforementioned social networks
						and WELOVENIGHT will not be responsible for the subsequent process
						that they may carry out with Users' Data. WELOVENIGHT recommends
						that Users visit the terms and conditions of use of these platforms
						prior to their interaction on social networks with the profiles of
						WELOVENIGHT in said social networks.
					</P>

					<P>
						The profiles on the social networks linked from this Platform have
						been created so that Users know better our activity and offer
						different communication channels aimed at people interested in our
						entity and the services we offer.
					</P>
					</li>

					<li className="space-y-4">
						<H2>Security Measures</H2>
					<P>
						WELOVENIGHT takes special care to ensure the security of Users'
						personal data. Users' Data is stored in the information systems of
						WELOVENIGHT, where it has adopted and implemented security,
						technical and organizational measures, to prevent any loss or
						unauthorized use by third parties, for example, the Platform of
						WELOVENIGHT use Https data encryption protocols.
					</P>
					</li>

					<li className="space-y-4">
						<H2>Which Are Users' Rights?</H2>
					<P>
						Users can request confirmation about whether their personal data are
						being processed and, if so, access them. Also, they can request the
						rectification of inaccurate data or, where appropriate, request its
						deletion when, among other reasons, the data are no longer necessary
						for the purposes for which it was collected.
					</P>

					<P>
						In certain circumstances, it is possible to request the limitation
						of the processing of Users' Data, in which case we will only keep
						them for the exercise of actions or the defense of claims.
					</P>

					<P>
						Users can revoke the consents given at any time. Once the revocation
						has been exercised, which will not be retroactive unless there is a
						legal provision that imposes this character, their Data may be
						conserved blocked during the limitation periods established by the
						applicable regulations.
					</P>

					<P>
						Remember that Users may also oppose the processing of their Data,
						particularly with respect to commercial communications, in which
						case we will not process the data, except for compelling legitimate
						reasons or for the exercise or defense of possible complaints.
					</P>

					<P>
						To exercise the aforementioned rights, Users should write to our
						privacy officer, attaching a copy of their identity document, to the
						following address:{` `}
						<A href="mailto:bielmartinez@welovenight.com">
							bielmartinez@welovenight.com
						</A>
						.
					</P>

					<P>
						To help us process Users' request, Users should indicate in the
						subject "Data Protection".
					</P>

					<P>
						Likewise, if Users have any questions regarding the exercise of
						their rights, before contacting the Control Authority, we remind
						them that they can contact us directly by writing to our privacy
						officer and we will try to help them in the best possible way.
					</P>

					<P>
						Finally, we remind Users that they have the right to file a claim
						with the competent authority on data protection (in Spain,{` `}
						<A
							href="https://www.aepd.es"
							target="_blank"
							rel="noopener noreferrer"
						>
							www.aepd.es
						</A>
						) in case they have knowledge or consider that a fact may involve a
						breach of the applicable regulations on data protection.
					</P>
					</li>
				</Ol>
			</div>
		</Container>
	)
}
